'use client';

import { useQuery } from '@tanstack/react-query';
import { getOrderLayers, getOrderLayersByOrder, getRunningOrderStoryIds } from '../actions/getOrderLayers';
import { pipelineKeys } from '../queryKeys';
import type { OrderLayersView } from '../order/types';
import { whileWaiting } from '@/shared/hooks/liveRefetch';

/**
 * The v2 order layers of a story. Live through SSE: `pipeline.layer.updated`
 * invalidates this key (registerPipelineEvents); a slow poll covers a dropped
 * stream while the order runs.
 *
 * data: undefined = not known yet, null = the story has no v2 order (an old
 * story: the v1 plan readers apply), else the order.
 */
export function useOrderLayers(storyId: string | null | undefined) {
  return useQuery<OrderLayersView | null>({
    queryKey: pipelineKeys.orderLayers(storyId ?? ''),
    queryFn: () => getOrderLayers(storyId!),
    enabled: !!storyId,
    staleTime: 2_000,
    // pipeline.layer.updated invalidates this key; the interval is only the fallback
    refetchInterval: (q) => whileWaiting(q.state.data?.status === 'running', 10_000),
  });
}

/** A `?plan=<id>` that may be a v2 order id → its story and view; null = a v1 plan id. */
export function useOrderByOrderId(orderId: string | null | undefined) {
  return useQuery({
    queryKey: pipelineKeys.order(orderId ?? ''),
    queryFn: () => getOrderLayersByOrder(orderId!),
    enabled: !!orderId,
    staleTime: 2_000,
  });
}

/** Story ids with a running v2 order (story list badge); SSE layer events refresh it. */
export function useRunningOrderStories(enabled = true) {
  return useQuery<string[]>({
    queryKey: pipelineKeys.running(),
    queryFn: () => getRunningOrderStoryIds(),
    enabled,
    staleTime: 5_000,
    refetchInterval: (q) => whileWaiting((q.state.data?.length ?? 0) > 0, 15_000),
  });
}
